<template>
  <div class="measure-dock" :class="{ 'measure-dock--narrow': view.narrow }" @keydown="$emit('panel-keydown', $event)">
    <!-- A div, not a <section>: app.scss gives every section a 4rem bottom margin with !important. -->
    <div role="region" :aria-label="$t('measure_tool.panel_a11y')" class="measure-panel">
      <div v-if="view.narrow" class="measure-panel__grabber" aria-hidden="true"></div>

      <div class="measure-panel__top">
        <div class="measure-panel__top-main">
          <h2 v-if="view.step !== 'placed'" class="measure-panel__title">{{ view.title }}</h2>
          <div v-else class="measure-stats">
            <div v-for="cell in view.stats" :key="cell.label" class="measure-stats__cell">
              <span class="measure-stats__label">{{ cell.label }}</span>
              <span class="measure-stats__value" :class="{ 'measure-stats__value--accent': cell.accent }">{{ cell.value }}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="measure-round-btn"
          :class="{ 'measure-round-btn--on': view.settings.open }"
          :aria-label="$t('measure_tool.settings_a11y')"
          :aria-expanded="view.settings.open"
          :title="$t('measure_tool.settings_title')"
          @click="$emit('toggle-settings')"
        >
          <span class="material-icons">tune</span>
        </button>
        <button type="button" class="measure-round-btn" :aria-label="$t('measure_tool.close_a11y')" :title="$t('measure_tool.close_a11y')" @click="$emit('close')">
          <span class="material-icons">close</span>
        </button>
      </div>

      <template v-if="view.step === 'start'">
        <div class="measure-choices">
          <button type="button" class="measure-choice" :disabled="view.myLocation.disabled" @click="$emit('use-my-location')">
            <span class="measure-choice__icon"><span class="material-icons measure-choice__icon--yellow">my_location</span></span>
            <span class="measure-choice__text">
              <span class="measure-choice__title">{{ $t('measure_tool.my_location') }}</span>
              <span class="measure-choice__detail">{{ view.myLocation.detail }}</span>
            </span>
          </button>
          <div class="measure-rule"></div>
          <button type="button" class="measure-choice" @click="$emit('pick-on-map')">
            <span class="measure-choice__icon"><span class="material-icons">location_on</span></span>
            <span class="measure-choice__text">
              <span class="measure-choice__title">{{ $t('measure_tool.choose_point') }}</span>
              <span class="measure-choice__detail">{{ view.pickDetail }}</span>
            </span>
          </button>
        </div>
        <p v-if="view.showRightClickTip" class="measure-tip">
          <span class="material-icons">mouse</span>{{ $t('measure_tool.right_click_tip') }}
        </p>
      </template>

      <button v-if="view.step === 'pickStart'" type="button" class="measure-pill-btn" @click="$emit('cancel-pick')">
        {{ $t('measure_tool.cancel') }}
      </button>

      <template v-if="view.step === 'placing'">
        <div v-if="view.from" class="measure-from">
          <span class="measure-from__label">{{ $t('measure_tool.from') }}</span>
          <span class="measure-from__chip">
            <span class="measure-from__dot" :class="{ 'measure-from__dot--me': view.from.isMyLocation }"></span>{{ view.from.name }}
          </span>
          <span class="measure-from__grid">{{ view.from.grid }}</span>
        </div>
        <div v-if="view.isPreview" class="measure-preview">
          <div class="measure-preview__tag"><span class="measure-preview__dot"></span>{{ $t('measure_tool.live_preview') }}</div>
          <div class="measure-stats">
            <div v-for="cell in view.stats" :key="cell.label" class="measure-stats__cell">
              <span class="measure-stats__label">{{ cell.label }}</span>
              <span class="measure-stats__value" :class="{ 'measure-stats__value--accent': cell.accent }">{{ cell.value }}</span>
            </div>
          </div>
        </div>
        <button type="button" class="measure-outline-btn" @click="$emit('open-range')">
          <span class="material-icons">explore</span>{{ $t('measure_tool.enter_range_bearing') }}
        </button>
      </template>

      <template v-if="view.step === 'range'">
        <p class="measure-range__intro">{{ view.range.intro }}</p>
        <div class="measure-range__fields">
          <label class="measure-range__field">{{ view.range.distanceLabel }}
            <input
              type="number"
              inputmode="decimal"
              :value="rangeDistance"
              @input="$emit('update:rangeDistance', $event.target.value)"
              @keydown="onRangeKey"
            >
          </label>
          <label class="measure-range__field">{{ view.range.bearingLabel }}
            <input
              type="number"
              inputmode="decimal"
              :value="rangeBearing"
              @input="$emit('update:rangeBearing', $event.target.value)"
              @keydown="onRangeKey"
            >
          </label>
        </div>
        <p class="measure-range__note">{{ view.range.note }}</p>
        <div class="measure-range__actions">
          <button type="button" class="measure-range__cancel" @click="$emit('close-range')">{{ $t('measure_tool.cancel') }}</button>
          <button type="button" class="measure-range__place" @click="$emit('place-range')">{{ $t('measure_tool.place_point') }}</button>
        </div>
      </template>

      <template v-if="view.step === 'placed' && view.placed">
        <div class="measure-profile">
          <svg
            :viewBox="`0 0 ${PROFILE_VIEWBOX.width} ${PROFILE_VIEWBOX.height}`"
            preserveAspectRatio="none"
            class="measure-profile__svg"
            role="img"
            :aria-label="view.placed.profile.label"
            @pointermove="onProfileMove"
            @pointerleave="$emit('profile-hover', null)"
          >
            <template v-if="view.placed.profile.status === 'ready'">
              <path :d="view.placed.profile.area" fill="#FFFFFF" opacity="0.14"></path>
              <path :d="view.placed.profile.line" fill="none" stroke="#FFFFFF" stroke-width="1.5" vector-effect="non-scaling-stroke"></path>
              <line
                v-if="view.placed.profile.hover"
                :x1="view.placed.profile.hover.x"
                :x2="view.placed.profile.hover.x"
                y1="0"
                :y2="PROFILE_VIEWBOX.height"
                stroke="#FDDF00"
                stroke-width="1.5"
                vector-effect="non-scaling-stroke"
              ></line>
            </template>
          </svg>
          <span v-if="view.placed.profile.status !== 'ready'" class="measure-profile__status">
            {{ view.placed.profile.status === 'loading' ? $t('measure_tool.profile_loading') : $t('measure_tool.profile_unavailable') }}
          </span>
          <div
            v-if="view.placed.profile.hover"
            class="measure-profile__tip"
            :style="{ left: `${view.placed.profile.hover.leftPercent}%`, transform: `translateX(${view.placed.profile.hover.shift})` }"
          >{{ view.placed.profile.hover.text }}</div>
        </div>
        <div class="measure-footer">
          <span class="measure-footer__grid">
            <span :title="view.placed.gridTitle" class="measure-footer__ref">{{ view.placed.gridText }}</span>
            <button
              type="button"
              class="measure-footer__copy"
              :class="{ 'measure-footer__copy--done': view.placed.copied }"
              :aria-label="$t('measure_tool.copy_grid_a11y')"
              @click="$emit('copy-grid')"
            >{{ view.placed.copyLabel }}</button>
          </span>
          <span :title="$t('measure_tool.time_title')" class="measure-footer__time">{{ view.placed.timeBack }}</span>
        </div>
      </template>
    </div>

    <div v-if="view.pills.show" class="measure-pills">
      <button v-if="view.pills.snap.show" type="button" role="switch" :aria-checked="view.pills.snap.on" class="measure-pill" @click="$emit('toggle-snap')">
        {{ $t('measure_tool.snap_to_route') }}
        <span class="measure-switch" :class="{ 'measure-switch--on': view.pills.snap.on }"><span class="measure-switch__knob"></span></span>
      </button>
      <button v-if="view.pills.createHike" type="button" class="measure-pill measure-pill--yellow measure-pill--end" :title="$t('measure_tool.create_hike_title')" @click="$emit('create-hike')">
        <span class="material-icons">add_location_alt</span>{{ $t('measure_tool.create_hike') }}
      </button>
      <div v-if="view.pills.lock.show" class="measure-lock">
        <button
          type="button"
          class="measure-pill measure-pill--lock"
          :class="{ 'measure-pill--open': view.pills.lock.menuOpen }"
          aria-haspopup="menu"
          :aria-expanded="view.pills.lock.menuOpen"
          @click="$emit('toggle-lock-menu')"
        >
          <span class="material-icons" :class="view.pills.lock.locked ? 'measure-lock__icon--locked' : 'measure-lock__icon'">{{ view.pills.lock.icon }}</span>
          {{ view.pills.lock.title }}
          <span class="material-icons measure-lock__chevron" :class="{ 'measure-lock__chevron--open': view.pills.lock.menuOpen }">expand_more</span>
        </button>
        <div v-if="view.pills.lock.menuOpen" role="menu" :aria-label="$t('measure_tool.lock_menu_a11y')" class="measure-lock__menu">
          <button
            v-for="item in view.pills.lock.items"
            :key="item.mode"
            type="button"
            role="menuitemradio"
            :aria-checked="item.on"
            class="measure-lock__item"
            :class="{ 'measure-lock__item--on': item.on }"
            @click="$emit('set-lock', item.mode)"
          >
            <span class="material-icons measure-lock__item-icon">{{ item.mode === 'Free' ? 'lock_open' : 'lock' }}</span>
            <span class="measure-lock__item-text">
              <span class="measure-lock__item-title">{{ item.title }}</span>
              <span class="measure-lock__item-detail">{{ item.detail }}</span>
            </span>
            <kbd v-if="item.kbd && !view.narrow" class="measure-kbd">{{ item.kbd }}</kbd>
          </button>
          <div class="measure-lock__rule"></div>
          <button type="button" role="menuitem" class="measure-lock__item" @click="$emit('open-range')">
            <span class="material-icons measure-lock__item-icon measure-lock__item-icon--yellow">explore</span>
            <span class="measure-lock__item-title">{{ $t('measure_tool.enter_range_bearing') }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="view.settings.open" role="dialog" :aria-label="$t('measure_tool.settings_title')" class="measure-settings">
      <div class="measure-settings__head">
        <h3>{{ $t('measure_tool.settings_title') }}</h3>
        <button type="button" class="measure-round-btn measure-round-btn--small" :aria-label="$t('measure_tool.close_settings_a11y')" @click="$emit('toggle-settings')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="measure-settings__group">
        <span class="measure-settings__label">{{ $t('measure_tool.north_reference') }}</span>
        <div role="group" :aria-label="$t('measure_tool.north_reference')" class="measure-segments measure-segments--3">
          <button
            v-for="seg in view.settings.north"
            :key="seg.value"
            type="button"
            :aria-pressed="seg.on"
            class="measure-segment"
            :class="{ 'measure-segment--on': seg.on }"
            @click="$emit('set-north', seg.value)"
          >{{ seg.label }}</button>
        </div>
        <span class="measure-settings__caption">{{ view.settings.northCaption }}</span>
      </div>
      <div class="measure-settings__group">
        <span class="measure-settings__label">{{ $t('measure_tool.bearing_unit') }}</span>
        <div role="group" :aria-label="$t('measure_tool.bearing_unit')" class="measure-segments measure-segments--2">
          <button
            v-for="seg in view.settings.units"
            :key="seg.value"
            type="button"
            :aria-pressed="seg.on"
            class="measure-segment"
            :class="{ 'measure-segment--on': seg.on }"
            @click="$emit('set-bearing-unit', seg.value)"
          >{{ seg.label }}</button>
        </div>
      </div>
      <label class="measure-settings__group measure-settings__label">{{ $t('measure_tool.grid_format') }}
        <select :value="view.settings.gridFormat" class="measure-settings__select" @change="$emit('set-grid-format', $event.target.value)">
          <option v-for="format in view.settings.gridFormats" :key="format.value" :value="format.value">{{ format.label }}</option>
        </select>
      </label>
      <div class="measure-settings__group">
        <span class="measure-settings__label">{{ $t('measure_tool.tolerance') }}</span>
        <div class="measure-tolerance">
          <button type="button" class="measure-round-btn" :aria-label="$t('measure_tool.tolerance_less_a11y')" @click="$emit('change-tolerance', -1)">
            <span class="material-icons">remove</span>
          </button>
          <span class="measure-tolerance__value">{{ view.settings.tolerance }}</span>
          <button type="button" class="measure-round-btn" :aria-label="$t('measure_tool.tolerance_more_a11y')" @click="$emit('change-tolerance', 1)">
            <span class="material-icons">add</span>
          </button>
          <span class="measure-settings__caption">{{ $t('measure_tool.tolerance_caption') }}</span>
        </div>
      </div>
      <div v-if="view.settings.showKeyboard" class="measure-settings__group">
        <span class="measure-settings__label">{{ $t('measure_tool.keyboard') }}</span>
        <ul class="measure-shortcuts">
          <li>{{ $t('measure_tool.shortcut_esc') }}</li>
          <li>{{ $t('measure_tool.shortcut_shift') }}</li>
          <li>{{ $t('measure_tool.shortcut_alt') }}</li>
          <li>{{ $t('measure_tool.shortcut_arrows') }}</li>
        </ul>
      </div>
      <p class="measure-settings__note">{{ $t('measure_tool.saved_in_browser') }}</p>
    </div>

    <div v-if="view.toast" role="status" class="measure-toast">
      <span class="material-icons">check_circle</span>{{ view.toast }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { PROFILE_VIEWBOX } from './viewTypes';
import type { MeasureView } from './viewTypes';

defineProps<{ view: MeasureView; rangeDistance: string; rangeBearing: string }>();

const emit = defineEmits([
  'close', 'toggle-settings', 'use-my-location', 'pick-on-map', 'cancel-pick', 'open-range', 'close-range',
  'place-range', 'update:rangeDistance', 'update:rangeBearing', 'copy-grid', 'profile-hover', 'toggle-snap',
  'create-hike', 'toggle-lock-menu', 'set-lock', 'set-north', 'set-bearing-unit', 'set-grid-format',
  'change-tolerance', 'panel-keydown',
]);

function onRangeKey(event: KeyboardEvent) {
  if (event.key === 'Enter') emit('place-range');
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close-range');
  }
}

function onProfileMove(event: PointerEvent) {
  const box = (event.currentTarget as Element).getBoundingClientRect();
  if (box.width <= 0) return;
  emit('profile-hover', Math.max(0, Math.min(1, (event.clientX - box.left) / box.width)));
}
</script>

<style scoped>
.measure-dock {
  --ink: #17181d;
  --tile: #2d2f35;
  --well: #22242a;
  --muted: #acb0bf;
  --accent: var(--yellow, #fddf00);
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 392px;
  max-height: calc(100% - 64px);
  color: #fff;
  letter-spacing: 0.02em;
}
.measure-dock--narrow {
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  width: auto;
  max-height: 72%;
  flex-direction: column-reverse;
}
.measure-panel {
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 16px 20px;
  border: 1px solid var(--tile);
  border-radius: 12px;
  background: var(--ink);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25), 0 16px 40px rgba(0, 0, 0, 0.3);
}
.measure-dock--narrow .measure-panel {
  padding: 10px 18px 20px;
  border: 0;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -6px 14px rgba(0, 0, 0, 0.25);
}
.measure-panel__grabber {
  width: 36px;
  height: 4px;
  margin: 0 auto 12px;
  border-radius: 2px;
  background: #505259;
}
.measure-panel__top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.measure-panel__top-main {
  flex: 1;
  min-width: 0;
}
.measure-panel__title {
  margin: 0;
  padding-top: 7px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}
.measure-round-btn {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--tile);
  color: #fff;
  cursor: pointer;
}
.measure-round-btn .material-icons {
  font-size: 18px;
}
.measure-round-btn--on {
  background: var(--accent);
  color: var(--ink);
}
.measure-round-btn--small {
  width: 30px;
  height: 30px;
}
.measure-round-btn:focus-visible,
.measure-pill:focus-visible,
.measure-choice:focus-visible,
.measure-segment:focus-visible,
.measure-lock__item:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.measure-stats {
  display: grid;
  grid-template-columns: auto auto auto;
  justify-content: space-between;
  column-gap: 12px;
}
.measure-stats__cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.measure-stats__label {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}
.measure-stats__value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}
.measure-stats__value--accent {
  color: var(--accent);
}
.measure-choices {
  margin-top: 6px;
}
.measure-choice {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 -8px;
  padding: 10px 8px;
  box-sizing: content-box;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.measure-choice:hover:not(:disabled) {
  background: var(--well);
}
.measure-choice:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.measure-choice__icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--tile);
}
.measure-choice__icon .material-icons {
  font-size: 19px;
}
.measure-choice__icon--yellow {
  color: var(--accent);
}
.measure-choice__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.measure-choice__title {
  font-size: 14px;
  font-weight: 700;
}
.measure-choice__detail {
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}
.measure-rule {
  height: 1px;
  background: var(--tile);
}
.measure-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}
.measure-tip .material-icons {
  font-size: 16px;
}
.measure-pill-btn {
  height: 34px;
  margin-top: 12px;
  padding: 0 16px;
  border: 0;
  border-radius: 100px;
  background: var(--tile);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.measure-from {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.measure-from__label {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}
.measure-from__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px 0 8px;
  border-radius: 100px;
  background: var(--tile);
  font-size: 11px;
  font-weight: 700;
}
.measure-from__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 2.5px #70c381;
}
.measure-from__dot--me {
  width: 10px;
  height: 10px;
  background: #2b7be4;
  box-shadow: 0 0 0 2px var(--accent);
}
.measure-from__grid {
  margin-left: auto;
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.measure-preview {
  margin-top: 12px;
  padding: 10px 12px 12px;
  border-radius: 10px;
  background: var(--well);
}
.measure-preview__tag {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: var(--accent);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.measure-preview__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
}
.measure-outline-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 38px;
  margin-top: 12px;
  border: 1.5px solid var(--accent);
  border-radius: 100px;
  background: transparent;
  color: var(--accent);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.measure-outline-btn .material-icons {
  font-size: 18px;
}
.measure-range__intro {
  margin: 4px 0 12px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}
.measure-range__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.measure-range__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}
.measure-range__field input {
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid #505259;
  border-radius: 8px;
  outline: none;
  background: var(--tile);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}
.measure-range__field input:focus {
  border-color: var(--accent);
}
.measure-range__note {
  margin: 10px 0 12px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
}
.measure-range__actions {
  display: flex;
  gap: 10px;
}
.measure-range__cancel,
.measure-range__place {
  flex: 1;
  height: 40px;
  border: 0;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.measure-range__cancel {
  background: var(--tile);
  color: #fff;
}
.measure-range__place {
  flex: 1.4;
  background: var(--accent);
  color: var(--ink);
}
.measure-profile {
  position: relative;
  margin-top: 14px;
}
.measure-profile__svg {
  display: block;
  width: 100%;
  height: 44px;
  cursor: crosshair;
}
.measure-profile__status {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}
.measure-profile__tip {
  position: absolute;
  bottom: 50px;
  padding: 5px 8px;
  border-radius: 6px;
  background: var(--accent);
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
}
.measure-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--tile);
}
.measure-footer__grid {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.measure-footer__ref {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.measure-footer__copy {
  margin: -4px 0;
  padding: 4px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--accent);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.measure-footer__copy--done {
  color: #4bc263;
}
.measure-footer__time {
  margin-left: auto;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.measure-pills {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}
.measure-dock--narrow .measure-pills {
  padding: 0 16px;
}
.measure-pill {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 6px 0 16px;
  border: 0;
  border-radius: 100px;
  background: var(--ink);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}
.measure-pill .material-icons {
  font-size: 18px;
}
.measure-pill--yellow {
  gap: 6px;
  padding: 0 16px 0 12px;
  background: var(--accent);
  color: var(--ink);
}
/* Desktop keeps the pills together under the panel; the phone sheet spreads them across, as the apps do. */
.measure-dock--narrow .measure-pill--end,
.measure-dock--narrow .measure-lock {
  margin-left: auto;
}
.measure-pill--lock {
  gap: 6px;
  padding: 0 10px 0 12px;
}
.measure-pill--open {
  background: var(--tile);
}
.measure-switch {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: 36px;
  height: 24px;
  border-radius: 100px;
  background: #505259;
}
.measure-switch--on {
  background: #4bc263;
}
.measure-switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.15s ease;
}
.measure-switch--on .measure-switch__knob {
  left: 15px;
}
.measure-lock {
  position: relative;
}
.measure-lock__icon {
  color: var(--muted);
}
.measure-lock__icon--locked {
  color: var(--accent);
}
.measure-lock__chevron {
  color: var(--muted);
  transition: transform 0.15s ease;
}
.measure-lock__chevron--open {
  transform: rotate(180deg);
}
.measure-lock__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 2;
  width: 290px;
  box-sizing: border-box;
  padding: 6px;
  border: 1px solid var(--tile);
  border-radius: 12px;
  background: var(--ink);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}
.measure-dock--narrow .measure-lock__menu {
  top: auto;
  bottom: calc(100% + 8px);
}
.measure-lock__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.measure-lock__item:hover {
  background: var(--well);
}
.measure-lock__item--on {
  background: rgba(253, 223, 0, 0.1);
}
.measure-lock__item-icon {
  color: var(--muted);
  font-size: 19px;
}
.measure-lock__item--on .measure-lock__item-icon,
.measure-lock__item-icon--yellow {
  color: var(--accent);
}
.measure-lock__item-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.measure-lock__item-title {
  font-size: 13px;
  font-weight: 700;
}
.measure-lock__item-detail {
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}
.measure-lock__rule {
  height: 1px;
  margin: 4px 8px;
  background: var(--tile);
}
.measure-kbd {
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--tile);
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
}
.measure-settings {
  position: absolute;
  top: 0;
  right: calc(100% + 10px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 300px;
  box-sizing: border-box;
  padding: 16px;
  border: 1px solid var(--tile);
  border-radius: 12px;
  background: var(--well);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}
.measure-dock--narrow .measure-settings {
  position: fixed;
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  width: auto;
  border: 0;
  border-radius: 16px 16px 0 0;
}
.measure-settings__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.measure-settings__head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.measure-settings__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.measure-settings__label {
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
}
.measure-settings__caption {
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
}
.measure-settings__select {
  height: 40px;
  padding: 0 10px;
  border: 1px solid #505259;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.measure-settings__note {
  margin: 0;
  padding-top: 10px;
  border-top: 1px solid var(--tile);
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
}
.measure-segments {
  display: grid;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background: var(--ink);
}
.measure-segments--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.measure-segments--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.measure-segment {
  height: 32px;
  padding: 0 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.measure-segment--on {
  background: var(--accent);
  color: var(--ink);
}
.measure-tolerance {
  display: flex;
  align-items: center;
  gap: 10px;
}
.measure-tolerance .measure-round-btn {
  width: 36px;
  height: 36px;
}
.measure-tolerance__value {
  min-width: 56px;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
}
.measure-tolerance .measure-settings__caption {
  flex: 1;
  line-height: 1.4;
}
.measure-shortcuts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin: 0;
  padding: 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 500;
  list-style: none;
}
.measure-toast {
  position: fixed;
  bottom: 48px;
  left: 50%;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 100px;
  background: var(--ink);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  transform: translateX(-50%);
}
.measure-toast .material-icons {
  color: #4bc263;
  font-size: 18px;
}
</style>
